import { dismissTradovateDiscoveredAccount, getTradovateConnections, getTradovateDiscoveredAccounts, useQuery } from "wasp/client/operations";
import { AlertTriangle, Plus, X } from "lucide-react";
import { Button } from "../../client/components/ui/button";
import useTranslation from "../../client/hooks/useTranslation";
import { cn } from "../../client/utils";

/**
 * Nouveaux comptes détectés côté broker (passage en funded, nouveau challenge…).
 * L'import est toujours confirmé par l'utilisateur, jamais silencieux. Un bandeau
 * par connexion (une prop firm chacune), pour importer sur la bonne connexion
 * avec sa prop firm pré-remplie. Partagé par la page Comptes Advanced et le Lite.
 */
export default function TradovateDiscoveredBanners({ onImport, className = "mb-6" }: {
  onImport: (connectionId: string, propFirmTemplateId: string | null) => void;
  className?: string;
}) {
  const t = useTranslation();
  // `{}` exactement : même entrée de cache que le reste de l'app.
  const { data: tradovateConnections = [] } = useQuery(getTradovateConnections, {});
  const { data: discoveredAccounts = [], refetch: refetchDiscovered } = useQuery(getTradovateDiscoveredAccounts);
  const handleDismissDiscovered = async (discoveredAccountId: string) => {
    try {
      await dismissTradovateDiscoveredAccount({ discoveredAccountId });
      refetchDiscovered();
    } catch (err) {
      console.error("dismiss discovered account failed", err);
    }
  };
  const openTradovateImportForExisting = onImport;

  return (
    <>
      {/* New broker-side accounts detected (funded transition, new challenge…) —
          import is always user-confirmed, never silent. Grouped per connection
          (one prop firm each) so each group imports onto the right connection
          with its own prop firm pre-filled for tier detection. */}
      {Array.from(
        (discoveredAccounts as any[]).reduce((map: Map<string, any[]>, d: any) => {
          const list = map.get(d.connectionId) ?? [];
          list.push(d);
          map.set(d.connectionId, list);
          return map;
        }, new Map<string, any[]>()),
      ).map(([connId, discovered]: [string, any[]]) => {
        const conn = (tradovateConnections as any[]).find((c: any) => c.id === connId);
        const propFirmName = conn?.propFirmTemplate?.name ?? t.tradovate.connectionBannerTitle;
        // Bannière d'avertissement dès qu'un compte du lot semble venir d'une
        // autre prop firm : l'import silencieux rattacherait des comptes
        // Tradeify à une connexion Lucid, sans rien signaler.
        const foreignCount = discovered.filter((d: any) => d.isForeign).length;
        const hasForeign = foreignCount > 0;
        return (
          <div
            key={connId}
            className={cn(
              `${className} space-y-2 rounded-lg border px-4 py-3`,
              hasForeign
                ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10"
                : "border-primary/30 bg-primary/5",
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">
                {hasForeign ? (
                  <span className="inline-flex items-center gap-1.5 text-[hsl(var(--warning))]">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {t.tradovate.foreignBannerTitle}
                  </span>
                ) : (
                  <>
                    {t.tradovate.discoveredBannerTitle.replace("{count}", String(discovered.length))}
                    <span className="ml-1 text-muted-foreground">· {propFirmName}</span>
                  </>
                )}
              </p>
              <Button
                size="sm"
                // Import volontairement TOUJOURS possible : la détection est un
                // faisceau d'indices, pas une preuve. On avertit, on ne bloque
                // pas un utilisateur qui sait ce qu'il fait.
                variant={hasForeign ? "outline" : "default"}
                onClick={() =>
                  openTradovateImportForExisting(connId, conn?.propFirmTemplate?.id ?? null)
                }
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t.tradovate.discoveredImport}
              </Button>
            </div>

            {hasForeign && (
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>{t.tradovate.foreignBannerBody.replace("{propFirm}", propFirmName)}</p>
                <p>{t.tradovate.foreignBannerHint}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {discovered.map((d: any) => (
                <span
                  key={d.id}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border bg-card px-2 py-0.5 text-xs",
                    d.isForeign
                      ? "border-[hsl(var(--warning))]/50"
                      : "border-border",
                  )}
                >
                  <span className="font-mono">{d.tradovateAccountName}</span>
                  {d.isForeign && (
                    <span className="text-[10px] text-[hsl(var(--warning))]">
                      {t.tradovate.foreignBadge}
                    </span>
                  )}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    title={t.tradovate.discoveredDismiss}
                    onClick={() => handleDismissDiscovered(d.id)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        );
      })}

    </>
  );
}
